// Capital requirement mappings for different products
export const cMap = {
  'SPDA': {
    'C0': 0, 'C3': .0047, 'C4': .02
  },
  'SPDA_PROCAP': {
    'C0': 0, 'C3': .0067, 'C4': .01
  },
  'FHLB': {
    'C0': 0.0028,'C3': .0047, 'C4': 0.02
  },
  'GAS Prepay': {
    'C0': 0, 'C3': .0047, 'C4': 0.02
  },
  'SFA': {
    'C0': 0.0106,'C3': .0047, 'C4': 0.02
  }
  // 'Keystone': {
  //   'C0': 0, 'C3': .005, 'C4': 0
  // }
};

/**
 * Determines the capital inputs column name based on product and region
 * @param {string} product - Product type
 * @param {string} region - Region
 * @returns {string} Capital inputs identifier
 */
export const getCapitalInputsKey = (product, region) => {
  if (product === 'SPDA') {
    if (region === 'NAT Procap') {
      return 'NAT_PROC';
    } else if (region === 'SCP' || region === 'NAT' || region === 'WAVG') {
      return 'NAT';
    } else if (region.includes('NY')) {
      return 'NY';
    } else if (region.includes('PROCAP')) {
      return 'NAT_PROC';
    }
    return 'NAT';
  } else if (product === 'FHLB') {
    return 'FHLB';
  } else if (product === 'GAS Prepay') {
    return 'GAS_PREPAY';
  } else if (product === 'SFA') {
    return 'SFA';
  }
  return 'NAT';
};

/**
 * Processes raw data from backend by applying capital inputs and calculating derived fields
 * @param {Array} roeData - Raw ROE data from backend
 * @param {Array} capitalInputsData - Capital inputs data from backend
 * @param {Array} covData - COV data from backend
 * @param {string} product - Product type
 * @param {string} region - Region
 * @returns {Array} Processed data with calculated fields
 */
export const processRawData = (roeData, capitalInputsData, covData, product, region) => {
  if (!roeData || !capitalInputsData || !covData) {
    return [];
  }

  const capitalInputsKey = getCapitalInputsKey(product, region);
  
  // Filter capital inputs to get the right row
  const capInputsRow = capitalInputsData.find(row => row.C1O === capitalInputsKey);
  const capInputsDict = capInputsRow || {};
  
  // Create COV dictionary mapping ASSETS to the capital_inputs column
  const covDict = {};
  covData.forEach(row => {
    if (row.ASSETS && row[capitalInputsKey] !== undefined) {
      covDict[row.ASSETS] = row[capitalInputsKey];
    }
  });
  
  const productList = [
    "CM1", "CM2", "CM3", "CM4", "CM5", "RML"
  ];
  
  const specialCov = ['Residual Tranches', 'LP Investment', 'Collateral Loans'];
  
  // Process each row in the ROE data
  const processedData = roeData.map(row => {
    const rating = row.RATING;
    
    // Calculate SIZE_FACTOR
    let SIZE_FACTOR;
    if (productList.includes(rating) || specialCov.includes(rating)) {
      SIZE_FACTOR = 1;
    } else {
      SIZE_FACTOR = capInputsDict.SIZE_FACTOR !== undefined ? capInputsDict.SIZE_FACTOR : 1;
    }
    
    // Calculate TAX
    let TAX;
    if (specialCov.includes(rating)) {
      TAX = 0.79;
    } else if (productList.includes(rating)) {
      TAX = capInputsDict.TAX_MORTGAGE !== undefined ? capInputsDict.TAX_MORTGAGE : 0;
    } else {
      TAX = capInputsDict.TAX !== undefined ? capInputsDict.TAX : 0;
    }
    
    // Calculate COV
    let COV;
    if (specialCov.includes(rating)) {
      COV = covDict[rating] !== undefined ? covDict[rating] : capInputsDict.COVAR;
    } else {
      COV = capInputsDict.COVAR !== undefined ? capInputsDict.COVAR : 1;
    }
    
    // Calculate derived fields
    const BASE_RBC = row.BASE_RBC || 0;
    const PRE_TARGET = BASE_RBC * SIZE_FACTOR * COV * TAX;
    
    const TARGET = 4;
    const EFF_RBC = TARGET * PRE_TARGET;
    
    const C1 = PRE_TARGET;
    
    return {
      ...row,
      SIZE_FACTOR,
      TAX,
      COV,
      PRE_TARGET,
      TARGET,
      EFF_RBC,
      C1
    };
  });
  
  return processedData;
};

/**
 * Computes weighted requirement (WT_REQ) for each rating based on tenor and product
 * @param {Array} data - Raw data array with RATING, TARGET, C1 columns
 * @param {string} product - Product type (e.g., 'SPDA', 'FHLB')
 * @param {string} tenor - Tenor value (e.g., '5Y', '7Y')
 * @param {string} region - Region (e.g., 'NAT', 'NY', 'WAVG')
 * @param {string} customTenor - Custom tenor value for WAVG All cases
 * @returns {Object} Object mapping RATING to {WT_REQ}
 */
export const computeData = (data, product, tenor, region, customTenor) => {
  if (!data || data.length === 0) {
    return {};
  }

  const tempComputed = {};
  
  data.forEach(row => {
    const { RATING, TARGET, C1 } = row;
    
    const C0 = cMap[product].C0;
    const C3 = cMap[product].C3;
    const C4 = cMap[product].C4;
    
    let ST_REQ = TARGET * (C1 + C3 + C4 + C0);
    
    let ten = tenor.replace('Y', '');
    
    if (region === 'WAVG' && tenor === 'All') {
      ten = customTenor;
    }
    
    if (product !== 'SPDA') {
      ten = '5';
    }
    
    const ST_WT = 1 / parseInt(ten);
    const LT_REQ = TARGET * (C1 + C3 + C0);
    const LT_WT = 1 - ST_WT;
    
    const WT_REQ = ST_REQ * ST_WT + LT_REQ * LT_WT;

    tempComputed[RATING] = { WT_REQ };
  });
  
  return tempComputed;
};

/**
 * Computes values for a single rating row
 * @param {string} rating - Rating code
 * @param {number} yld - Yield percentage
 * @param {Object} computedData - Pre-computed data with WT_REQ per rating
 * @param {number} COF - Cost of funds percentage
 * @param {number} PAMFees - PAM fees percentage
 * @param {number} costOfDebt - Cost of debt percentage
 * @param {number} debtToCapital - Debt to capital percentage
 * @param {number} equityMultiplier - Equity multiplier
 * @param {number} taxRate - Tax rate percentage
 * @returns {Object} Calculated values for the row
 */
export const computeRowValues = (
  rating, 
  yld, 
  computedData, 
  COF, 
  PAMFees, 
  costOfDebt, 
  debtToCapital, 
  equityMultiplier, 
  taxRate
) => {
  if (!computedData || !computedData[rating]) {
    return {
      netYield: 'N/A',
      capital: 'N/A',
      capitalDecimal: null,
      naicLeverage: 'N/A',
      unleveredROE: 'N/A',
      leveredROE: 'N/A'
    };
  }
  
  // Convert percentages to decimals where applicable
  const decYld = yld / 100;
  const decCOF = COF / 100;
  const decFEE = PAMFees / 100;
  const decCOD = costOfDebt / 100;
  const decDTC = debtToCapital / 100;
  const decTAX = taxRate / 100;
  const EQM = equityMultiplier;

  const capitalDecimal = computedData[rating].WT_REQ; 
  
  const net = decYld - decFEE;
  
  if (!capitalDecimal || capitalDecimal <= 0) {
    // Can't compute NAIC if capital is zero or null
    return {
      netYield: `${(net * 100).toFixed(2)}%`,
      capital: 'N/A',
      capitalDecimal: null,
      naicLeverage: 'N/A',
      unleveredROE: 'N/A',
      leveredROE: 'N/A'
    };
  }

  const NAIC = 1 / capitalDecimal - 1;
  
  const unlev = (net + (net - decCOF)*NAIC)*(1 - decTAX);
  
  const lev = (unlev * EQM) - (EQM * decCOD * decDTC * (1 - decTAX));

  return {
    netYield: `${(net * 100).toFixed(2)}%`,
    capital: `${(capitalDecimal * 100).toFixed(2)}%`,
    capitalDecimal: capitalDecimal,
    naicLeverage: `${NAIC.toFixed(1)}x`,
    unleveredROE: `${(unlev * 100).toFixed(1)}%`,
    leveredROE: `${(lev * 100).toFixed(1)}%`
  };
};

/**
 * Computes the blend row values based on weighted averages
 * @param {Array} normalRowData - Array of row data with computed values
 * @param {number} sumProductCapital - Sum of (weight * wal * capital) across rows
 * @param {number} sumProductWeightWAL - Sum of (weight * wal) across rows
 * @param {number} COF - Cost of funds percentage
 * @param {number} PAMFees - PAM fees percentage
 * @param {number} costOfDebt - Cost of debt percentage
 * @param {number} debtToCapital - Debt to capital percentage
 * @param {number} equityMultiplier - Equity multiplier
 * @param {number} taxRate - Tax rate percentage
 * @returns {Object} Calculated blend values
 */
export const computeBlendValues = (
  normalRowData,
  sumProductCapital,
  sumProductWeightWAL,
  COF,
  PAMFees,
  costOfDebt,
  debtToCapital,
  equityMultiplier,
  taxRate
) => {
  const decCOF = COF / 100;
  const decCOD = costOfDebt / 100;
  const decDTC = debtToCapital / 100;
  const decTAX = taxRate / 100;
  const decFEE = PAMFees / 100;
  const EQM = equityMultiplier;

  const capitalDecimal = sumProductCapital / sumProductWeightWAL;
  
  const blendYield = normalRowData.reduce((acc, r) => {
    if (!isNaN(r.weight) && !isNaN(r.wal) && !isNaN(r.yld)) {
      const contribution = (r.weight / 100) * r.wal * r.yld;
      return acc + contribution;
    }
    return acc;
  }, 0) / sumProductWeightWAL;

  const yld = blendYield / 100;
  const net = yld - decFEE;

  if (!capitalDecimal || capitalDecimal <= 0) {
    return {
      yld: `${(yld * 100).toFixed(2)}%`,
      netYield: `${(net * 100).toFixed(2)}%`,
      capital: 'N/A',
      naicLeverage: 'N/A',
      unleveredROE: 'N/A',
      leveredROE: 'N/A'
    };
  }

  const NAIC = 1 / capitalDecimal - 1;
  
  const unlev = (net + (net - decCOF)*NAIC)*(1 - decTAX);
  
  const lev = (unlev * EQM) - (EQM * decCOD * decDTC * (1 - decTAX));

  return {
    yld: `${(yld * 100).toFixed(2)}%`,
    netYield: `${(net * 100).toFixed(2)}%`,
    capital: `${(capitalDecimal * 100).toFixed(2)}%`,
    naicLeverage: `${NAIC.toFixed(1)}x`,
    unleveredROE: `${(unlev * 100).toFixed(1)}%`,
    leveredROE: `${(lev * 100).toFixed(1)}%`
  };
};

/**
 * Calculate sum of (weight * wal * capital) for blend row capital calculation
 * @param {Array} normalRowData - Array of row data with computed values
 * @returns {number} Sum of products
 */
export const calculateSumProductCapital = (normalRowData) => {
  return normalRowData.reduce((acc, r) => {
    if (r.capitalDecimal !== null && !isNaN(r.capitalDecimal) && !isNaN(r.weight) && !isNaN(r.wal)) {
      return acc + ((r.weight / 100) * r.wal * r.capitalDecimal);
    }
    return acc;
  }, 0);
};

/**
 * Calculate sum of (weight * wal) for blend row weight calculation
 * @param {Array} normalRowData - Array of row data with computed values
 * @returns {number} Sum of products
 */
export const calculateSumProductWeightWAL = (normalRowData) => {
  return normalRowData.reduce((acc, r) => {
    if (!isNaN(r.weight) && !isNaN(r.wal)) {
      return acc + ((r.weight / 100) * r.wal);
    }
    return acc;
  }, 0);
};

